import * as React from 'react';
import ShellSkeleton from './components/ShellSkeleton';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { oktaAuth } from './auth';
import { ColorModeProvider } from './ColorModeContext';
import CallbackPage from './pages/CallbackPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import SignedOutPage from './pages/SignedOutPage';
// @ts-expect-error JS module
import NewChat from './pages/NewChat';

// Lazy-loaded routes: split the heavy pages (DesignView, AdminUsage) and
// the unauthenticated demo pages into separate chunks so the home page
// (NewChat) loads immediately. Each lazy route gets its own JS chunk,
// loaded on-demand when the user navigates to it.
const DesignView = React.lazy(() => import('./pages/DesignView'));
const AdminUsage = React.lazy(() => import('./pages/AdminUsage'));
const CanvasDemoPage = React.lazy(() => import('./pages/CanvasDemoPage'));
const ComponentsDemoPage = React.lazy(() => import('./pages/ComponentsDemoPage'));

// Suspense fallback: the app's frame as a shimmer skeleton — the user sees
// structure instantly while lazy chunks load, instead of a spinner cascade.
const SuspenseFallback = () => <ShellSkeleton />;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  // Track whether we've already kicked off a redirect to avoid React 18
  // StrictMode firing it twice on mount, and to avoid a re-render loop if
  // the parent oscillates between authed/unauthed during token refresh.
  const redirectingRef = React.useRef(false);
  // Watchdog: the skeleton must never be a dead end. If auth hasn't
  // resolved after 15s (hung Okta call, stuck logout sentinel, a
  // signInWithRedirect that failed silently), surface a real sign-in
  // button instead of an infinite shimmer. Anchored to the moment the
  // route first became non-ready (stuckSinceRef) rather than restarting
  // per effect run — Okta's autoRenew retry loop can emit auth-state
  // flips more often than the timeout, and a per-flip restart would
  // keep the card from ever appearing.
  const [authStuck, setAuthStuck] = React.useState(false);
  const stuckSinceRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      stuckSinceRef.current = null;
      setAuthStuck(false);
      return undefined;
    }
    if (stuckSinceRef.current == null) stuckSinceRef.current = Date.now();
    const remaining = Math.max(0, 15_000 - (Date.now() - stuckSinceRef.current));
    const t = setTimeout(() => setAuthStuck(true), remaining);
    return () => clearTimeout(t);
  }, [isLoading, isAuthenticated]);

  React.useEffect(() => {
    // Don't fire signInWithRedirect while a logout is in progress —
    // tokenManager.clear() flips isAuthenticated to false BEFORE the
    // browser navigates to /signed-out, and we don't want this effect
    // to win the race (it bounces the user through silent SSO right
    // back into the app, which reads as "sign out doesn't work").
    // SignedOutPage clears the sentinel on mount.
    if (sessionStorage.getItem('ajo:logout-in-progress')) return;
    if (isLoading || isAuthenticated || redirectingRef.current) return;
    redirectingRef.current = true;
    // Canonical Adobe Agentic Apps flow (Confluence "Authentication for
    // Agentic Apps", flows B+C): always start PKCE directly from the
    // app — no portal bounce. Okta silently reuses an existing SSO
    // session if one is alive (e.g. user just logged into the portal),
    // or shows its hosted login when none. Bouncing unauthenticated
    // users to the portal before kicking off PKCE produces a redirect
    // loop because the portal sees the same Okta session and forwards
    // them right back to the app, which still has no local tokens.
    oktaAuth.signInWithRedirect();
  }, [isLoading, isAuthenticated]);

  if (isLoading || !isAuthenticated) {
    if (authStuck) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #F7F7F8)' }}>
          <div style={{ textAlign: 'center', maxWidth: 380, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #1A1A1A)', marginBottom: 8 }}>
              Sign-in didn’t complete
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary, #5A5A66)', marginBottom: 16, lineHeight: 1.5 }}>
              Your session may have expired. Click below to sign in again.
            </div>
            <button
              type="button"
              style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--primary, #1A1A1A)', color: 'var(--surface, #fff)', fontFamily: 'inherit' }}
              onClick={() => {
                try { sessionStorage.removeItem('ajo:logout-in-progress'); } catch { /* best-effort */ }
                redirectingRef.current = true;
                oktaAuth.signInWithRedirect();
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      );
    }
    // Same skeleton as boot/lazy-load — one continuous frame from first
    // paint through auth instead of a spinner cascade.
    return <ShellSkeleton />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login/callback" element={<CallbackPage />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/signed-out" element={<SignedOutPage />} />
      {/* Unauthenticated preview of the new BlockNote canvas. Lives
          outside ProtectedRoute so a developer can eyeball the editor
          on localhost without Okta tokens or a real design. Lazy-loaded
          since it's not on the critical path. */}
      <Route
        path="/canvas-demo"
        element={
          <React.Suspense fallback={<SuspenseFallback />}>
            <CanvasDemoPage />
          </React.Suspense>
        }
      />
      {/* Phase 4 chat/canvas component preview (CheckpointCard,
          AgentQuestion, SectionStatusChip). Unauth, eyeball-only. */}
      <Route
        path="/components-demo"
        element={
          <React.Suspense fallback={<SuspenseFallback />}>
            <ComponentsDemoPage />
          </React.Suspense>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <NewChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/design/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<SuspenseFallback />}>
              <DesignView />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      {/* Usage-analytics dashboard. Link visibility is gated by
          config.adminGroups; the API's ADMIN_GROUPS check is the real gate,
          so deep-linking non-admins just see a 403 alert. */}
      <Route
        path="/admin/usage"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<SuspenseFallback />}>
              <AdminUsage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // /canvas-demo is the unauthenticated phase-1 preview surface for the
  // BlockNote canvas. It must run in a tree that doesn't mount
  // AuthProvider — the provider's OktaAuth subscription fails on
  // localhost and tears the page down before any route renders. Detect
  // the path before BrowserRouter so we never even instantiate the
  // auth-coupled routes when the user is just here to eyeball the editor.
  // The lazy import still applies (keeps it out of the main bundle), but
  // we skip the BrowserRouter wrapping.
  if (typeof window !== 'undefined' && window.location.pathname === '/canvas-demo') {
    return (
      <ColorModeProvider>
        <React.Suspense fallback={<SuspenseFallback />}>
          <CanvasDemoPage />
        </React.Suspense>
      </ColorModeProvider>
    );
  }
  if (typeof window !== 'undefined' && window.location.pathname === '/components-demo') {
    return (
      <ColorModeProvider>
        <React.Suspense fallback={<SuspenseFallback />}>
          <ComponentsDemoPage />
        </React.Suspense>
      </ColorModeProvider>
    );
  }
  return (
    <ColorModeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ColorModeProvider>
  );
}
